<template>
	<Teleport to="body">
		<div
			v-if="visible"
			class="g-image-gallery-lightbox"
			@click.self="close"
		>
			<button
				type="button"
				class="g-image-gallery-lightbox__close"
				aria-label="关闭"
				@click="close"
			>
				<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
					<path
						d="M7 7l10 10M17 7L7 17"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
					/>
				</svg>
			</button>
			<div
				ref="carousel"
				class="g-image-gallery-lightbox__carousel"
				:class="{ 'is-zoomed': isZoomed }"
				@touchstart.passive="onTouchStart"
				@touchmove="onTouchMove"
				@touchend="onTouchEnd"
				@touchcancel="onTouchEnd"
			>
				<div
					class="g-image-gallery-lightbox__track"
					:class="{ 'is-dragging': isTouchDragging }"
					:style="trackStyle"
				>
					<div
						v-for="(url, index) in urls"
						:key="`${url}-${index}`"
						class="g-image-gallery-lightbox__slide"
					>
						<img
							:src="url"
							alt=""
							draggable="false"
							:style="index === activeIndex ? imageStyle : undefined"
						/>
					</div>
				</div>
			</div>
			<div v-if="urls.length > 1" class="g-image-gallery-lightbox__count">
				{{ activeIndex + 1 }}/{{ urls.length }}
			</div>
		</div>
	</Teleport>
</template>

<script>
	const SWIPE_MIN_PX = 40;
	const SWIPE_RATIO = 0.18;
	const MIN_SCALE = 1;
	const MAX_SCALE = 4;
	const ZOOM_EPS = 1.05;

	export default {
		name: 'GImageGalleryLightbox',
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			urls: {
				type: Array,
				default: () => [],
			},
			initialIndex: {
				type: Number,
				default: 0,
			},
		},
		emits: ['update:visible', 'close'],
		data() {
			return {
				activeIndex: 0,
				touchStartX: 0,
				touchStartY: 0,
				isTouchDragging: false,
				dragOffset: 0,
				carouselWidth: 0,
				scale: 1,
				translateX: 0,
				translateY: 0,
				isPinching: false,
				isPanning: false,
				pinchStartDistance: 0,
				pinchStartScale: 1,
				pinchStartTranslateX: 0,
				pinchStartTranslateY: 0,
				pinchStartMidX: 0,
				pinchStartMidY: 0,
				panStartX: 0,
				panStartY: 0,
				panOriginX: 0,
				panOriginY: 0,
			};
		},
		computed: {
			isZoomed() {
				return this.scale > ZOOM_EPS;
			},
			canSwipe() {
				return this.urls.length > 1 && !this.isZoomed && !this.isPinching;
			},
			trackStyle() {
				const baseOffset = this.activeIndex * 100;
				if (!this.isTouchDragging || !this.carouselWidth) {
					return {
						transform: `translateX(-${baseOffset}%)`,
					};
				}
				return {
					transform: `translateX(calc(-${baseOffset}% + ${this.dragOffset}px))`,
				};
			},
			imageStyle() {
				const animating = this.isPinching || this.isPanning || this.isTouchDragging;
				return {
					transform: `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`,
					transition: animating ? 'none' : 'transform 0.2s ease',
				};
			},
		},
		watch: {
			visible(val) {
				if (val) {
					this.activeIndex = Math.min(
						Math.max(0, this.initialIndex),
						Math.max(0, this.urls.length - 1),
					);
					this.resetZoom();
					this.resetTouchState();
					document.body.style.overflow = 'hidden';
				} else {
					document.body.style.overflow = '';
				}
			},
			urls() {
				this.activeIndex = 0;
				this.resetZoom();
				this.resetTouchState();
			},
			activeIndex() {
				this.resetZoom();
			},
		},
		beforeUnmount() {
			document.body.style.overflow = '';
		},
		methods: {
			close() {
				this.$emit('update:visible', false);
				this.$emit('close');
			},
			getCarouselWidth() {
				return this.$refs.carousel?.offsetWidth || 0;
			},
			getTouchDistance(touches) {
				const [a, b] = touches;
				return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
			},
			getTouchMidpoint(touches) {
				const [a, b] = touches;
				return {
					x: (a.clientX + b.clientX) / 2,
					y: (a.clientY + b.clientY) / 2,
				};
			},
			clampScale(scale) {
				return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
			},
			resetZoom() {
				this.scale = 1;
				this.translateX = 0;
				this.translateY = 0;
				this.isPinching = false;
				this.isPanning = false;
			},
			resetTouchState() {
				this.isTouchDragging = false;
				this.dragOffset = 0;
				this.isPinching = false;
				this.isPanning = false;
			},
			startPinch(touches) {
				this.isPinching = true;
				this.isPanning = false;
				this.isTouchDragging = false;
				this.dragOffset = 0;
				this.pinchStartDistance = this.getTouchDistance(touches) || 1;
				this.pinchStartScale = this.scale;
				this.pinchStartTranslateX = this.translateX;
				this.pinchStartTranslateY = this.translateY;
				const mid = this.getTouchMidpoint(touches);
				this.pinchStartMidX = mid.x;
				this.pinchStartMidY = mid.y;
			},
			startPan(touch) {
				this.isPanning = true;
				this.isTouchDragging = false;
				this.dragOffset = 0;
				this.panStartX = touch.clientX;
				this.panStartY = touch.clientY;
				this.panOriginX = this.translateX;
				this.panOriginY = this.translateY;
			},
			onTouchStart(event) {
				const { touches } = event;
				if (touches.length >= 2) {
					this.startPinch(touches);
					return;
				}
				if (touches.length !== 1) return;
				const touch = touches[0];
				if (this.isZoomed) {
					this.startPan(touch);
					return;
				}
				if (!this.canSwipe) return;
				this.touchStartX = touch.clientX;
				this.touchStartY = touch.clientY;
				this.isTouchDragging = false;
				this.dragOffset = 0;
				this.carouselWidth = this.getCarouselWidth();
			},
			onTouchMove(event) {
				const { touches } = event;
				if (touches.length >= 2) {
					if (!this.isPinching) {
						this.startPinch(touches);
					}
					event.preventDefault();
					const distance = this.getTouchDistance(touches) || 1;
					const nextScale = this.clampScale(
						this.pinchStartScale * (distance / this.pinchStartDistance),
					);
					const mid = this.getTouchMidpoint(touches);
					const scaleRatio = nextScale / (this.pinchStartScale || 1);
					this.scale = nextScale;
					this.translateX = this.pinchStartTranslateX * scaleRatio
						+ (mid.x - this.pinchStartMidX);
					this.translateY = this.pinchStartTranslateY * scaleRatio
						+ (mid.y - this.pinchStartMidY);
					return;
				}

				if (touches.length === 1 && this.isPinching) {
					this.isPinching = false;
					if (this.scale > ZOOM_EPS) {
						this.startPan(touches[0]);
					}
					return;
				}

				if (touches.length === 1 && (this.isPanning || this.isZoomed)) {
					event.preventDefault();
					if (!this.isPanning) {
						this.startPan(touches[0]);
					}
					const touch = touches[0];
					this.translateX = this.panOriginX + (touch.clientX - this.panStartX);
					this.translateY = this.panOriginY + (touch.clientY - this.panStartY);
					return;
				}

				if (!this.canSwipe || touches.length !== 1) return;
				const touch = touches[0];
				const deltaX = touch.clientX - this.touchStartX;
				const deltaY = touch.clientY - this.touchStartY;
				if (!this.isTouchDragging) {
					if (Math.abs(deltaX) < 8) return;
					if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
					this.isTouchDragging = true;
				}
				event.preventDefault();
				let offset = deltaX;
				if (this.activeIndex === 0 && offset > 0) {
					offset *= 0.35;
				} else if (this.activeIndex === this.urls.length - 1 && offset < 0) {
					offset *= 0.35;
				}
				this.dragOffset = offset;
			},
			onTouchEnd(event) {
				const remain = event.touches?.length || 0;
				if (remain >= 2) return;

				if (remain === 1 && this.isPinching) {
					this.isPinching = false;
					if (this.scale > ZOOM_EPS) {
						this.startPan(event.touches[0]);
					} else {
						this.resetZoom();
					}
					return;
				}

				if (this.isPinching || this.isPanning) {
					this.isPinching = false;
					this.isPanning = false;
					if (this.scale < ZOOM_EPS) {
						this.resetZoom();
					}
					return;
				}

				if (!this.canSwipe) {
					this.resetTouchState();
					return;
				}
				if (this.isTouchDragging) {
					const threshold = Math.max(
						SWIPE_MIN_PX,
						this.carouselWidth * SWIPE_RATIO,
					);
					if (this.dragOffset < -threshold && this.activeIndex < this.urls.length - 1) {
						this.activeIndex += 1;
					} else if (this.dragOffset > threshold && this.activeIndex > 0) {
						this.activeIndex -= 1;
					}
				}
				this.resetTouchState();
			},
		},
	};
</script>

<style scoped lang="less">
	.g-image-gallery-lightbox {
		position: fixed;
		inset: 0;
		z-index: 2000;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(0, 0, 0, 0.92);
	}

	.g-image-gallery-lightbox__close {
		position: absolute;
		top: calc(12px + env(safe-area-inset-top, 0));
		right: calc(12px + env(safe-area-inset-right, 0));
		z-index: 2;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		padding: 0;
		border: none;
		border-radius: 50%;
		background: rgba(255, 255, 255, 0.15);
		color: #fff;
		cursor: pointer;
		box-sizing: border-box;

		svg {
			display: block;
			width: 18px;
			height: 18px;
		}

		&:active {
			background: rgba(255, 255, 255, 0.25);
		}
	}

	.g-image-gallery-lightbox__carousel {
		width: 100%;
		max-height: 100%;
		overflow: hidden;
		/* 手势由脚本处理：滑动切图 + 双指缩放 */
		touch-action: none;
	}

	.g-image-gallery-lightbox__track {
		display: flex;
		align-items: center;
		transition: transform 0.25s ease;

		&.is-dragging {
			transition: none;
		}
	}

	.g-image-gallery-lightbox__slide {
		flex: 0 0 100%;
		min-width: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0 0.24rem;
		box-sizing: border-box;
		overflow: hidden;

		img {
			display: block;
			max-width: 100%;
			max-height: 80vh;
			object-fit: contain;
			transform-origin: center center;
			will-change: transform;
			user-select: none;
			-webkit-user-drag: none;
			touch-action: none;
		}
	}

	.g-image-gallery-lightbox__count {
		position: absolute;
		left: 50%;
		bottom: calc(24px + env(safe-area-inset-bottom, 0));
		transform: translateX(-50%);
		padding: 4px 12px;
		border-radius: 12px;
		background: rgba(0, 0, 0, 0.5);
		color: #fff;
		font-size: 14px;
		line-height: 1.4;
		pointer-events: none;
	}
</style>
